import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, BarChart3, Loader2,
  Plus, Sparkles, Send, Upload, FileText, Target, Trash2, RefreshCw,
  ArrowUpRight, ArrowDownRight, Minus, Brain, Shield, PieChart,
} from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

let ApexChart = null;
try { ApexChart = require("react-apexcharts").default; } catch (e) { /* ApexCharts not available */ }

const SUB_TABS = [
  { key: "dashboard", label: "Tổng quan", icon: BarChart3 },
  { key: "costs", label: "Chi phí", icon: FileText },
  { key: "pnl", label: "Lãi/Lỗ", icon: PieChart },
  { key: "roi", label: "ROI", icon: Target },
  { key: "insights", label: "AI Phân tích", icon: Brain },
  { key: "forecast", label: "Dự báo", icon: TrendingUp },
  { key: "decision", label: "Mô phỏng", icon: Target },
];

const CAT_LABELS = {
  salary: "Quỹ lương", operating: "Vận hành", legal: "Pháp lý", soft: "Chi phí mềm",
};

function fmtVND(n) {
  if (!n) return "0đ";
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(0)}K`;
  return `${n.toLocaleString("vi-VN")}đ`;
}

function fmtFull(n) { return n ? `${n.toLocaleString("vi-VN")}đ` : "0đ"; }

export default function FinancePanel() {
  const [subTab, setSubTab] = useState("dashboard");
  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="finance-panel">
      <div className="flex gap-1 px-4 py-2 border-b border-[#1e293b] bg-[#0a0f1a] overflow-x-auto">
        {SUB_TABS.map(t => (
          <button key={t.key} onClick={() => setSubTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${subTab === t.key ? "bg-[#1e293b] text-emerald-400" : "text-slate-500 hover:text-slate-300"}`}
            data-testid={`finance-tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {subTab === "dashboard" && <DashboardTab />}
        {subTab === "costs" && <CostsTab />}
        {subTab === "pnl" && <PnLTab />}
        {subTab === "roi" && <ROITab />}
        {subTab === "insights" && <InsightsTab />}
        {subTab === "forecast" && <ForecastTab />}
        {subTab === "decision" && <DecisionTab />}
      </div>
    </div>
  );
}

// ==================== DASHBOARD ====================
function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/finance/dashboard?period=${period}`); setData(r.data); }
    catch { toast.error("Lỗi tải dữ liệu"); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>;
  if (!data) return null;

  const ChangeIcon = ({ val }) => val > 0 ? <ArrowUpRight className="w-3 h-3 text-emerald-400" /> : val < 0 ? <ArrowDownRight className="w-3 h-3 text-red-400" /> : <Minus className="w-3 h-3 text-slate-500" />;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="finance-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-400" /> Tài chính & Lợi nhuận</h2>
        <div className="flex gap-1">
          {["week", "month", "year"].map(p => (
            <button key={p} onClick={() => setPeriod(p)} className={`px-2.5 py-1 rounded text-[10px] ${period === p ? "bg-emerald-500/20 text-emerald-400" : "text-slate-500"}`}>
              {p === "week" ? "Tuần" : p === "month" ? "Tháng" : "Năm"}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards — Valex Sale Card Style with Sparkline */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "DOANH THU", value: data.total_revenue, change: data.rev_change, gradient: "from-[#4680ff] to-[#5b8def]", sparkData: data.daily_trend?.map(d => d.revenue) || [] },
          { label: "CHI PHI", value: data.total_cost, change: data.cost_change, gradient: "from-[#fc5286] to-[#fd6d8e]", sparkData: data.daily_trend?.map(d => d.cost) || [] },
          { label: "LOI NHUAN", value: data.profit, change: data.profit_change, gradient: "from-[#05c3a6] to-[#22d6b4]", sparkData: data.daily_trend?.map(d => d.profit) || [] },
          { label: "BIEN LN", value: `${data.margin}%`, change: null, gradient: "from-[#fd7e14] to-[#ff9a44]", sparkData: data.daily_trend?.map((d, i) => (i + 1) * Math.abs(data.margin || 1)) || [] },
        ].map((m, i) => (
          <div key={i} className={`relative overflow-hidden rounded-xl bg-gradient-to-b ${m.gradient} p-4 min-h-[160px] flex flex-col justify-between shadow-lg`}>
            <div>
              <p className="text-[10px] font-bold text-white/80 tracking-wider uppercase">{m.label}</p>
              <p className="text-2xl font-bold text-white mt-2">{typeof m.value === "number" ? fmtVND(m.value) : m.value}</p>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-white/70">So kỳ trước</span>
              {m.change !== null && (
                <span className="flex items-center gap-1 bg-white/20 rounded-full px-2 py-0.5 text-[10px] text-white font-medium">
                  {m.change > 0 ? "+" : ""}{m.change}%
                </span>
              )}
            </div>
            {/* Sparkline */}
            {ApexChart && m.sparkData.length > 0 && (
              <div className="absolute bottom-0 left-0 right-0 opacity-40">
                <ApexChart type="area" height={60} width="100%"
                  options={{
                    chart: { sparkline: { enabled: true }, animations: { enabled: true, easing: 'easeinout', speed: 1200 } },
                    stroke: { curve: 'smooth', width: 2 },
                    fill: { type: 'gradient', gradient: { opacityFrom: 0.5, opacityTo: 0, stops: [0, 100] } },
                    colors: ['#ffffff'],
                    tooltip: { enabled: false },
                  }}
                  series={[{ data: m.sparkData.slice(-14) }]}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Alerts */}
      {data.alerts?.length > 0 && (
        <div className="space-y-1.5">
          {data.alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${a.type === "danger" ? "bg-red-500/5 border-red-500/20" : "bg-amber-500/5 border-amber-500/20"}`}>
              <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${a.type === "danger" ? "text-red-400" : "text-amber-400"}`} />
              <span className="text-xs text-slate-300">{a.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Revenue Trend Chart — ApexCharts Valex Style */}
      {data.daily_trend?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-3">Xu hướng 14 ngày</p>
          {ApexChart ? (
            <ApexChart
              type="area"
              height={200}
              options={{
                chart: { toolbar: { show: false }, sparkline: { enabled: false }, animations: { enabled: true, easing: 'easeinout', speed: 800 } },
                stroke: { curve: 'smooth', width: [2.5, 2.5, 2], dashArray: [0, 0, 5] },
                fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05, stops: [0, 95, 100] } },
                colors: ['#0162e8', '#22c03c', '#fbbc0b'],
                xaxis: { categories: data.daily_trend.map(d => d.date.slice(5)), labels: { style: { fontSize: '10px' } } },
                yaxis: { labels: { formatter: v => fmtVND(v), style: { fontSize: '10px' } } },
                grid: { borderColor: 'var(--default-border, #1e293b)', strokeDashArray: 3 },
                legend: { position: 'bottom', fontSize: '11px', labels: { useSeriesColors: true } },
                tooltip: { y: { formatter: v => fmtFull(v) } },
                dataLabels: { enabled: false },
              }}
              series={[
                { name: 'Doanh thu', data: data.daily_trend.map(d => d.revenue) },
                { name: 'Chi phí', data: data.daily_trend.map(d => d.cost) },
                { name: 'Lợi nhuận', data: data.daily_trend.map(d => d.profit) },
              ]}
            />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 9 }} tickFormatter={v => v.slice(5)} />
                <YAxis tick={{ fill: "#64748b", fontSize: 9 }} tickFormatter={v => fmtVND(v)} />
                <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 11, color: "#e2e8f0" }} formatter={v => fmtFull(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="revenue" name="Doanh thu" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="cost" name="Chi phí" stroke="#f59e0b" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="profit" name="Lợi nhuận" stroke="#84cc16" strokeWidth={2} dot={false} strokeDasharray="5 5" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      {/* Revenue & Cost Breakdown + KiotViet Comparison */}
      <KiotVietComparison />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-2">Nguồn doanh thu</p>
          {data.revenue_breakdown?.map((r, i) => (
            <div key={i} className="flex justify-between py-1.5 border-b border-[#334155]/30 last:border-0">
              <span className="text-xs text-slate-300">{r.source}</span>
              <span className="text-xs font-bold text-emerald-400">{fmtFull(r.amount)}</span>
            </div>
          ))}
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-2">Phân bổ chi phí</p>
          {data.cost_breakdown?.length > 0 ? data.cost_breakdown.map((c, i) => (
            <div key={i} className="flex justify-between py-1.5 border-b border-[#334155]/30 last:border-0">
              <span className="text-xs text-slate-300">{c.label}</span>
              <span className="text-xs font-bold text-amber-400">{fmtFull(c.amount)}</span>
            </div>
          )) : <p className="text-[10px] text-slate-600">Chưa có chi phí. Nhập từ tab "Chi phí".</p>}
        </div>
      </div>
    </div>
  );
}

// ==================== COSTS ====================
function CostsTab() {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ category: "operating", subcategory: "", amount: 0, date: new Date().toISOString().slice(0, 10), note: "" });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [uploading, setUploading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/finance/costs?month=${month}`); setCosts(data.costs || []); }
    catch {}
    finally { setLoading(false); }
  }, [month]);
  useEffect(() => { fetch(); }, [fetch]);

  const submit = async () => {
    if (!form.amount) { toast.error("Nhập số tiền"); return; }
    try { await api.post("/finance/costs", form); toast.success("Đã thêm chi phí"); setAddOpen(false); fetch(); }
    catch { toast.error("Lỗi"); }
  };

  const del = async (id) => {
    try { await api.delete(`/finance/costs/${id}`); fetch(); } catch {}
  };

  const clearAll = async () => {
    if (!window.confirm("Xoá TẤT CẢ chi phí tháng này?")) return;
    try { await api.delete(`/finance/costs/clear?month=${month}`); toast.success("Đã xoá tất cả"); fetch(); } catch { toast.error("Lỗi"); }
  };

  const seedSample = async () => {
    try { const { data } = await api.post("/finance/costs/seed-sample"); toast.success(data.message); fetch(); } catch { toast.error("Lỗi"); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/finance/costs/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.message); fetch();
    } catch { toast.error("Lỗi import"); }
    finally { setUploading(false); }
  };

  const total = costs.reduce((s, c) => s + (c.amount || 0), 0);

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="costs-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><FileText className="w-5 h-5 text-amber-400" /> Quản lý chi phí</h2>
        <div className="flex gap-2 items-center">
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="bg-[#1e293b] border-[#334155] text-slate-300 h-8 text-xs w-36" />
          <button onClick={seedSample} className="text-[10px] text-slate-500 hover:text-emerald-400 border border-[#334155] rounded px-2 py-1">Mẫu</button>
          <button onClick={clearAll} className="text-[10px] text-slate-500 hover:text-red-400 border border-[#334155] rounded px-2 py-1">Xoá hết</button>
          <label className="cursor-pointer">
            <input type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
            <Button variant="outline" className="border-[#334155] text-slate-300 h-8 text-xs gap-1" disabled={uploading} asChild><span><Upload className="w-3.5 h-3.5" /> {uploading ? "Đang import..." : "Import Excel"}</span></Button>
          </label>
          <Button onClick={() => setAddOpen(true)} className="bg-emerald-500 hover:bg-emerald-400 text-[#0f172a] h-8 text-xs gap-1"><Plus className="w-3.5 h-3.5" /> Thêm</Button>
        </div>
      </div>

      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 flex items-center justify-between">
        <span className="text-xs text-slate-400">Tổng chi phí tháng {month}</span>
        <span className="text-lg font-bold text-amber-400">{fmtFull(total)}</span>
      </div>

      {loading ? <Loader2 className="w-5 h-5 text-emerald-400 animate-spin mx-auto mt-4" /> :
      costs.length === 0 ? <p className="text-slate-500 text-center py-8 text-sm">Chưa có chi phí. Bấm "Thêm" hoặc "Import Excel".</p> :
      <div className="space-y-1.5">
        {costs.map(c => (
          <div key={c.id} className="flex items-center justify-between bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <Badge className={`text-[8px] border-0 ${c.category === "salary" ? "bg-purple-500/20 text-purple-400" : c.category === "operating" ? "bg-amber-500/20 text-amber-400" : c.category === "legal" ? "bg-sky-500/20 text-sky-400" : "bg-slate-500/20 text-slate-400"}`}>
                {CAT_LABELS[c.category] || c.category}
              </Badge>
              <span className="text-xs text-slate-200">{c.note || c.subcategory || "—"}</span>
              <span className="text-[10px] text-slate-600">{c.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-amber-400">{fmtFull(c.amount)}</span>
              <button onClick={() => del(c.id)} className="text-red-400/50 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
            </div>
          </div>
        ))}
      </div>}

      {/* Add Cost Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] text-slate-200 max-w-sm">
          <DialogHeader><DialogTitle className="text-emerald-400">Thêm chi phí</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div><Label className="text-slate-400 text-[10px]">Danh mục</Label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full mt-0.5 bg-[#0f172a] border border-[#334155] rounded-md px-3 py-2 text-sm text-slate-200">
                {Object.entries(CAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><Label className="text-slate-400 text-[10px]">Chi tiết</Label><Input value={form.subcategory} onChange={e => setForm({...form, subcategory: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" placeholder="VD: Điện tháng 4" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-slate-400 text-[10px]">Số tiền (VNĐ)</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: +e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
              <div><Label className="text-slate-400 text-[10px]">Ngày</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            </div>
            <div><Label className="text-slate-400 text-[10px]">Ghi chú</Label><Input value={form.note} onChange={e => setForm({...form, note: e.target.value})} className="bg-[#0f172a] border-[#334155] text-slate-200 mt-0.5 h-8 text-xs" /></div>
            <Button onClick={submit} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#0f172a] font-semibold h-9 text-xs">Lưu chi phí</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== PNL ====================
function PnLTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState("month");

  useEffect(() => {
    setLoading(true);
    api.get(`/finance/pnl?group_by=${groupBy}`).then(r => { setData(r.data.pnl || []); setLoading(false); }).catch(() => setLoading(false));
  }, [groupBy]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="pnl-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><PieChart className="w-5 h-5 text-emerald-400" /> Báo cáo Lãi/Lỗ</h2>
        <div className="flex gap-1">
          {[["day", "Ngày"], ["week", "Tuần"], ["month", "Tháng"]].map(([k, l]) => (
            <button key={k} onClick={() => setGroupBy(k)} className={`px-2.5 py-1 rounded text-[10px] ${groupBy === k ? "bg-emerald-500/20 text-emerald-400" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>

      {data.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          {ApexChart ? (
            <ApexChart
              type="bar"
              height={220}
              options={{
                chart: { toolbar: { show: false }, animations: { enabled: true, easing: 'easeinout', speed: 600 } },
                plotOptions: { bar: { borderRadius: 4, columnWidth: '55%', dataLabels: { position: 'top' } } },
                colors: ['#0162e8', '#fbbc0b', '#22c03c'],
                xaxis: { categories: data.map(d => d.period.length > 7 ? d.period.slice(5) : d.period), labels: { style: { fontSize: '10px' } } },
                yaxis: { labels: { formatter: v => fmtVND(v), style: { fontSize: '10px' } } },
                grid: { borderColor: 'var(--default-border, #1e293b)', strokeDashArray: 3 },
                legend: { position: 'bottom', fontSize: '11px' },
                tooltip: { y: { formatter: v => fmtFull(v) } },
                dataLabels: { enabled: false },
              }}
              series={[
                { name: 'Doanh thu', data: data.map(d => d.revenue) },
                { name: 'Chi phí', data: data.map(d => d.cost) },
                { name: 'Lợi nhuận', data: data.map(d => d.profit) },
              ]}
            />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="period" tick={{ fill: "#64748b", fontSize: 9 }} tickFormatter={v => v.length > 7 ? v.slice(5) : v} />
                <YAxis tick={{ fill: "#64748b", fontSize: 9 }} tickFormatter={v => fmtVND(v)} />
                <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 11, color: "#e2e8f0" }} formatter={v => fmtFull(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="revenue" name="Doanh thu" fill="#10b981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cost" name="Chi phí" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="profit" name="Lợi nhuận" fill="#84cc16" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      <div className="space-y-1">
        {data.map((d, i) => (
          <div key={i} className="flex items-center justify-between bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2">
            <span className="text-xs text-slate-300 font-mono w-20">{d.period}</span>
            <span className="text-xs text-emerald-400">{fmtVND(d.revenue)}</span>
            <span className="text-xs text-amber-400">{fmtVND(d.cost)}</span>
            <span className={`text-xs font-bold ${d.profit >= 0 ? "text-lime-400" : "text-red-400"}`}>{fmtVND(d.profit)}</span>
            <span className={`text-[10px] ${d.margin >= 30 ? "text-lime-400" : d.margin >= 0 ? "text-amber-400" : "text-red-400"}`}>{d.margin}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== ROI ====================
function ROITab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/finance/roi").then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-emerald-400 animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="roi-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Target className="w-5 h-5 text-emerald-400" /> Phân tích ROI</h2>
        <span className="text-[10px] text-slate-500">{data.period}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.roi?.map((item, i) => {
          const isPositive = item.roi > 0;
          return (
            <div key={i} className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid={`roi-card-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-200">{item.service}</h3>
                <div className={`px-2 py-0.5 rounded text-xs font-bold ${isPositive ? "bg-emerald-500/20 text-emerald-400" : item.roi === 0 ? "bg-slate-500/20 text-slate-400" : "bg-red-500/20 text-red-400"}`}>
                  ROI: {item.roi}%
                </div>
              </div>
              <div className="space-y-1.5">
                {item.revenue !== undefined && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Doanh thu</span>
                    <span className="text-emerald-400 font-medium">{fmtFull(item.revenue)}</span>
                  </div>
                )}
                {item.revenue_attributed !== undefined && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Doanh thu quy đổi</span>
                    <span className="text-emerald-400 font-medium">{fmtFull(item.revenue_attributed)}</span>
                  </div>
                )}
                {item.cost_estimate !== undefined && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Chi phí ước tính</span>
                    <span className="text-amber-400 font-medium">{fmtFull(item.cost_estimate)}</span>
                  </div>
                )}
                {item.investment !== undefined && (
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-400">Đầu tư</span>
                    <span className="text-amber-400 font-medium">{fmtFull(item.investment)}</span>
                  </div>
                )}
              </div>
              {/* ROI Bar */}
              <div className="mt-3 h-2 bg-[#0f172a] rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${isPositive ? "bg-emerald-500" : "bg-red-500"}`}
                  style={{ width: `${Math.min(Math.abs(item.roi), 200) / 2}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {data.roi?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
          <p className="text-xs text-slate-400 mb-3">So sánh ROI theo dịch vụ</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data.roi}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="service" tick={{ fill: "#64748b", fontSize: 10 }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 9 }} tickFormatter={v => `${v}%`} />
              <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8, fontSize: 11, color: "#e2e8f0" }} formatter={v => `${v}%`} />
              <Bar dataKey="roi" name="ROI %" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

// ==================== AI INSIGHTS ====================
function InsightsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try { const { data } = await api.post("/finance/insights"); setData(data); }
    catch { toast.error("Lỗi"); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="insights-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Brain className="w-5 h-5 text-emerald-400" /> AI Phân tích tài chính</h2>
        <Button onClick={generate} disabled={loading} className="bg-emerald-500 hover:bg-emerald-400 text-[#0f172a] gap-1.5 text-xs" data-testid="generate-insights-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Phân tích
        </Button>
      </div>

      {!data && !loading && (
        <div className="text-center py-12"><Brain className="w-10 h-10 text-slate-600 mx-auto mb-3" /><p className="text-sm text-slate-500">Bấm "AI Phân tích" để tạo báo cáo.</p></div>
      )}

      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500">Sức khoẻ tài chính</p>
              <p className={`text-2xl font-bold ${data.health_score >= 70 ? "text-emerald-400" : data.health_score >= 40 ? "text-amber-400" : "text-red-400"}`}>{data.health_score}/100</p>
            </div>
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3 text-center">
              <p className="text-[9px] text-slate-500">Rủi ro lớn nhất</p>
              <p className="text-xs text-red-400 mt-1">{data.top_risk}</p>
            </div>
          </div>

          {data.insights?.map((ins, i) => (
            <div key={i} className={`rounded-xl p-3 border ${ins.type === "danger" ? "bg-red-500/5 border-red-500/20" : ins.type === "warning" ? "bg-amber-500/5 border-amber-500/20" : ins.type === "success" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-[#1e293b] border-[#334155]"}`}>
              <p className="text-sm font-medium text-slate-200">{ins.title}</p>
              <p className="text-xs text-slate-400 mt-0.5">{ins.detail}</p>
              {ins.action && <p className="text-[10px] text-emerald-400 mt-1">Gợi ý: {ins.action}</p>}
            </div>
          ))}

          {data.top_opportunity && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3">
              <p className="text-[9px] text-emerald-400 uppercase">Cơ hội lớn nhất</p>
              <p className="text-xs text-slate-200 mt-0.5">{data.top_opportunity}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== FORECAST ====================
function ForecastTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try { const { data } = await api.post("/finance/forecast"); setData(data); }
    catch { toast.error("Lỗi"); }
    finally { setLoading(false); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="forecast-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" /> Dự báo tài chính</h2>
        <Button onClick={generate} disabled={loading} className="bg-emerald-500 hover:bg-emerald-400 text-[#0f172a] gap-1.5 text-xs">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} AI Dự báo
        </Button>
      </div>

      {!data && !loading && <div className="text-center py-12"><TrendingUp className="w-10 h-10 text-slate-600 mx-auto mb-3" /><p className="text-sm text-slate-500">Bấm "AI Dự báo" để xem dự báo.</p></div>}

      {data && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge className={`text-[9px] border-0 ${data.trend === "up" ? "bg-emerald-500/20 text-emerald-400" : data.trend === "down" ? "bg-red-500/20 text-red-400" : "bg-slate-500/20 text-slate-400"}`}>
              {data.trend === "up" ? "Tăng trưởng" : data.trend === "down" ? "Giảm" : "Ổn định"}
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
              <p className="text-[9px] text-emerald-400 uppercase mb-2">Dự báo tuần tới</p>
              <p className="text-xl font-bold text-emerald-400">{fmtVND(data.next_week?.revenue)}</p>
              <p className="text-[10px] text-slate-400 mt-1">Lợi nhuận: <span className="text-lime-400">{fmtVND(data.next_week?.profit)}</span></p>
              <p className="text-[10px] text-slate-400">Bookings: ~{data.next_week?.bookings}</p>
              <p className="text-[9px] text-slate-600 mt-1">Độ tin cậy: {data.next_week?.confidence}%</p>
            </div>
            <div className="bg-sky-500/5 border border-sky-500/20 rounded-xl p-4">
              <p className="text-[9px] text-sky-400 uppercase mb-2">Dự báo tháng tới</p>
              <p className="text-xl font-bold text-sky-400">{fmtVND(data.next_month?.revenue)}</p>
              <p className="text-[10px] text-slate-400 mt-1">Lợi nhuận: <span className="text-lime-400">{fmtVND(data.next_month?.profit)}</span></p>
              <p className="text-[9px] text-slate-600 mt-1">Độ tin cậy: {data.next_month?.confidence}%</p>
            </div>
          </div>

          <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-3">
            <p className="text-xs text-slate-300">{data.reasoning}</p>
          </div>

          {data.recommendations?.length > 0 && (
            <div className="space-y-1">
              {data.recommendations.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-slate-300 bg-[#0f172a] rounded-lg px-3 py-2">
                  <Shield className="w-3 h-3 text-emerald-400 shrink-0" /> {r}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== DECISION MODE ====================
function DecisionTab() {
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const ask = async () => {
    if (!question.trim()) return;
    setLoading(true);
    try { const { data } = await api.post(`/finance/decision?question=${encodeURIComponent(question)}`); setResult(data); }
    catch { toast.error("Lỗi"); }
    finally { setLoading(false); }
  };

  const presets = [
    "Nếu giảm giá sân 10% thì sao?",
    "Nếu tăng giá sân cuối tuần 20%?",
    "Nếu cắt chi phí marketing 50%?",
    "Nếu thuê thêm 1 HLV?",
    "Nếu mở thêm dịch vụ cafe sáng?",
  ];

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4" data-testid="decision-tab">
      <h2 className="text-lg font-bold text-slate-200 flex items-center gap-2"><Target className="w-5 h-5 text-emerald-400" /> Mô phỏng quyết định</h2>
      <p className="text-xs text-slate-500">Hỏi AI bất kỳ kịch bản tài chính — AI sẽ mô phỏng tác động.</p>

      <div className="flex gap-2">
        <Input value={question} onChange={e => setQuestion(e.target.value)} onKeyDown={e => e.key === "Enter" && ask()}
          placeholder="VD: Nếu giảm giá sân 10% thì sao?"
          className="bg-[#1e293b] border-[#334155] text-slate-200 h-9 text-sm flex-1" data-testid="decision-input" />
        <Button onClick={ask} disabled={loading} className="bg-emerald-500 hover:bg-emerald-400 text-[#0f172a] h-9 gap-1 text-xs">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Hỏi
        </Button>
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {presets.map((p, i) => (
          <button key={i} onClick={() => { setQuestion(p); }} className="text-[10px] text-slate-400 bg-[#1e293b] border border-[#334155] px-2.5 py-1 rounded-lg hover:text-emerald-400 hover:border-emerald-500/30">{p}</button>
        ))}
      </div>

      {result && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-slate-200">{result.scenario}</p>

          <div className="grid grid-cols-3 gap-2">
            <div className="bg-[#0f172a] rounded-lg p-2 text-center">
              <p className="text-[9px] text-slate-500">Doanh thu</p>
              <p className={`text-xs font-bold ${result.revenue_impact >= 0 ? "text-emerald-400" : "text-red-400"}`}>{result.revenue_impact >= 0 ? "+" : ""}{fmtVND(result.revenue_impact)}</p>
            </div>
            <div className="bg-[#0f172a] rounded-lg p-2 text-center">
              <p className="text-[9px] text-slate-500">Chi phí</p>
              <p className={`text-xs font-bold ${result.cost_impact <= 0 ? "text-emerald-400" : "text-amber-400"}`}>{result.cost_impact >= 0 ? "+" : ""}{fmtVND(result.cost_impact)}</p>
            </div>
            <div className="bg-[#0f172a] rounded-lg p-2 text-center">
              <p className="text-[9px] text-slate-500">Lợi nhuận</p>
              <p className={`text-xs font-bold ${result.profit_impact >= 0 ? "text-lime-400" : "text-red-400"}`}>{result.profit_impact >= 0 ? "+" : ""}{fmtVND(result.profit_impact)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-500">Tác động KH</p><p className="text-[10px] text-slate-300 mt-0.5">{result.customer_impact}</p></div>
            <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[9px] text-slate-500">Thời gian</p><p className="text-[10px] text-slate-300 mt-0.5">{result.timeline}</p></div>
          </div>

          <div className="flex items-center gap-2">
            <Badge className={`text-[8px] border-0 ${result.risk_level === "low" ? "bg-emerald-500/20 text-emerald-400" : result.risk_level === "high" ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"}`}>
              Rủi ro: {result.risk_level === "low" ? "Thấp" : result.risk_level === "high" ? "Cao" : "Trung bình"}
            </Badge>
          </div>

          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2">
            <p className="text-[9px] text-emerald-400 uppercase">Khuyến nghị</p>
            <p className="text-xs text-slate-200 mt-0.5">{result.recommendation}</p>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== KIOTVIET COMPARISON ====================
function KiotVietComparison() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/kiotviet/comparison?days=14").then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data || !data.chart_data?.some(d => d.kiotviet > 0)) return null;
  return (
    <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-400">So sánh: KiotViet vs Booking vs Kế toán</p>
        {data.alerts?.length > 0 && <Badge className="text-[8px] bg-amber-500/20 text-amber-400 border-0">{data.alerts.length} chênh lệch</Badge>}
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[8px] text-slate-600">KiotViet</p><p className="text-sm font-bold text-[#4680ff]">{fmtVND(data.totals?.kiotviet || 0)}</p></div>
        <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[8px] text-slate-600">Booking</p><p className="text-sm font-bold text-emerald-400">{fmtVND(data.totals?.booking || 0)}</p></div>
        <div className="bg-[#0f172a] rounded-lg p-2"><p className="text-[8px] text-slate-600">Kế toán</p><p className="text-sm font-bold text-amber-400">{fmtVND(data.totals?.accounting || 0)}</p></div>
      </div>
      {ApexChart && (
        <ApexChart type="line" height={160} options={{
          chart: { toolbar: { show: false }, animations: { enabled: true, speed: 600 } },
          stroke: { curve: 'smooth', width: [2.5, 2, 2], dashArray: [0, 0, 5] },
          colors: ['#4680ff', '#22c03c', '#fbbc0b'],
          xaxis: { categories: data.chart_data.map(d => d.date?.slice(5) || ""), labels: { style: { fontSize: '9px' } } },
          yaxis: { labels: { formatter: v => fmtVND(v), style: { fontSize: '9px' } } },
          grid: { borderColor: '#334155', strokeDashArray: 3 },
          legend: { position: 'bottom', fontSize: '10px' },
          tooltip: { y: { formatter: v => fmtFull(v) } },
          dataLabels: { enabled: false },
        }} series={[
          { name: 'KiotViet', data: data.chart_data.map(d => d.kiotviet) },
          { name: 'Booking', data: data.chart_data.map(d => d.booking) },
          { name: 'Kế toán', data: data.chart_data.map(d => d.accounting) },
        ]} />
      )}
    </div>
  );
}
